/**
 * English strings of Cài đặt › Kết nối › API báo cáo (docs/specs/reporting-api.md §12, §13). The
 * Vietnamese file must have exactly the same keys. Plain words: no scope, cursor, endpoint, token,
 * JSON, dataset, view, webhook or lease. Names of Twenty's own steps come from `lib/reportingApi.ts`.
 */
export const reporting = {
  title: 'Reporting API',
  intro:
    'Lets a customer management system (such as Twenty) and internal reporting tools read business data. Read only. Identity papers, birth dates, personal tax codes, electronic identification data and domain dossiers are never sent.',
  loadFailed: 'Could not load the places that receive data',
  production: 'This is the production server: every key must name the addresses it may be used from.',
  yes: 'Yes',
  no: 'No',
  close: 'Close',
  copy: 'Copy',
  copied: 'Copied',
  more: 'More',
  scope: {
    customers: { label: 'Customers', hint: 'Customer accounts and kind of customer.' },
    domains: { label: 'Domains', hint: 'Domains, their state, expiry date and dossier state.' },
    orders: { label: 'Orders', hint: 'Orders and each item, with amounts before tax, tax and fees collected for VNNIC.' },
    invoices: { label: 'Invoices', hint: 'Internal documents and credit notes, line by line.' },
    payments: { label: 'Payments', hint: 'Top-ups and payments.' },
    refunds: { label: 'Refunds', hint: 'Refunds.' },
    balances: { label: 'Wallet balances', hint: 'Customer wallet balances and how old they are.' },
    stats: { label: 'Daily figures', hint: 'Registrations, renewals and revenue per day, extension and channel.' },
  },
  dataset: {
    customers: 'Customers',
    domains: 'Domains',
    orders: 'Orders',
    order_items: 'Order items',
    invoices: 'Invoices',
    invoice_lines: 'Invoice lines',
    payments: 'Payments',
    refunds: 'Refunds',
    balances: 'Wallet balances',
    daily_stats: 'Daily figures',
  },
  places: {
    title: 'Places that receive data',
    description: 'Each place has its own keys and keeps its own position: replacing a key does not start it again.',
    add: 'Add a place',
    empty: 'No place receives data yet',
    emptyHint: 'Add one (for example "Twenty CRM"), choose what it receives and create a key.',
  },
  place: {
    receives: 'Data received',
    nothing: 'Nothing yet',
    contacts: 'Customer e-mail and phone',
    weekly: 'Everything is sent again every week',
    addedBy: 'Added by {{name}} · {{when}}',
    added: 'Added {{when}}',
    state: { RECEIVING: 'Receiving', NOTHING: 'Receives nothing yet', NO_KEY: 'No key yet', DISABLED: 'Disabled' },
    syncTitle: 'Up to date',
    syncNone: 'Starts with the first fetch.',
    sync: {
      never: '{{label}}: not fetched yet',
      catchingUp: '{{label}}: about {{rows}} rows left, done in about {{minutes}} minutes',
      catchingUpNoEta: '{{label}}: about {{rows}} rows left',
      lagging: '{{label}}: {{minutes}} minutes behind',
      upToDate: '{{label}}: up to date',
    },
  },
  warning: {
    KEY_EXPIRING: 'A key expires soon',
    KEY_WITHOUT_ADDRESSES: 'A key is not limited to addresses',
    KEY_SUSPENDED: 'A key is suspended',
    NOT_FETCHED_2H: '{{name}} has not fetched {{label}} for more than 2 hours',
    LAGGING_1H: '{{name}} is more than 1 hour behind on {{label}}',
    PARKED_ROWS: '{{count}} rows of {{label}} were not taken by {{name}}',
  },
  keys: {
    title: 'Keys',
    empty: 'No key yet. Create one for each use (scheduled fetch, fetch when called).',
    col: { key: 'Key', purpose: 'Used for', createdBy: 'Created by', lastUsed: 'Last used', expires: 'Expires', state: 'Status' },
    neverUsed: 'Never used',
    from: 'from {{ip}}',
    anyAddress: 'Any address',
    addresses_one: '{{count}} address',
    addresses_other: '{{count}} addresses',
    expiresOn: '{{date}}',
    revokedOn: 'Revoked {{when}}',
    replaces: 'Replaces an earlier key',
  },
  purpose: { SCHEDULED: 'Scheduled fetch', TRIGGERED: 'Fetch when called' },
  keyState: {
    ACTIVE: 'In use',
    EXPIRING: 'Expires soon',
    SUSPENDED: 'Suspended',
    EXPOSED: 'Exposed, being revoked',
    REVOKED: 'Revoked',
    EXPIRED: 'Expired',
  },
  suspended: {
    BUDGET_EXCEEDED: 'Fetched more than the daily limit',
    NETWORK_NOT_ALLOWED: 'Used from an address that is not allowed',
    EXPOSED_IN_URL: 'Sent inside a web address',
    OTHER: 'Suspended by the system',
  },
  add: {
    title: 'Add a place that receives data',
    description: 'A new place receives nothing and gets no customer e-mail or phone until you choose.',
    name: 'Name of the place',
    namePlaceholder: 'Twenty CRM',
    note: 'Note',
    submit: 'Add',
    done: 'Added. Now choose what it receives and create a key.',
    exists: 'A place with this name already exists.',
    stepReceive: '1. Data received',
    stepKey: '2. Key',
    nextKey: 'Next: create a key',
    later: 'Later',
    keyAfterContacts: 'The customer e-mail and phone option is waiting for a second person. Create the key after it is approved.',
  },
  receive: {
    action: 'Change data received',
    title: 'Data received by {{name}}',
    description: 'A change is sent for approval first.',
    kinds: 'Kinds of data',
    contacts: 'Include e-mail, phone and names of individual customers',
    contactsHelp:
      "This is personal data. Turn it on only when people must contact customers from {{name}}. It applies to every customer except those who asked not to be shared. Another person with the right to handle personal data must approve it.",
    confirmIntro: 'Confirm each point before sending:',
    confirm: {
      hosting: '{{name}} runs in Viet Nam, on servers Tino manages.',
      run_history: 'The run history of workflows at {{name}} is kept for at most 7 days.',
      viewers: 'Only administrators of {{name}} can see the run history of workflows.',
    },
    contactsOff:
      'When turned off, the customer e-mail, phone and names already sent are removed from the records at {{name}} on the next fetch. The run history of workflows at {{name}} must be cleared separately.',
    removed: '{{name}} will receive no more {{list}}. What was already sent stays at {{name}}; delete it there if it is not needed.',
    missing: '{{label}}: links to {{parents}} arrive as plain codes because {{name}} does not receive them.',
    weekly: 'Send everything again every week to fix differences',
    weeklyHint: 'Repairs records edited or deleted by hand at {{name}}.',
    noChange: 'Change something first.',
    submit: 'Send for approval',
  },
  key: {
    create: 'Create key',
    title: 'Create a key for {{name}}',
    description: 'The key is created once the request is approved, and shown only once.',
    name: 'Key name',
    namePlaceholder: 'Twenty – main server',
    purpose: 'Used for',
    purposeHint: {
      SCHEDULED: 'The workflow that runs every 15 minutes.',
      TRIGGERED: 'The workflow that fetches the rest at once when there is more.',
    },
    addresses: 'Only accept from addresses',
    addressesPlaceholder: '203.0.113.10\n203.0.113.0/24',
    addressesHelp: 'One address or range per line.',
    addressesRequired: 'The address of the {{name}} server. Required.',
    addressesOptional: 'Leave empty and the key works from any address. Not recommended.',
    addressesInvalid: 'Not an address or range: {{list}}',
    addressesTooMany: 'At most {{max}} addresses.',
    expires: 'Expires after',
    expiry: { '90d': '90 days', '6m': '6 months', '1y': '1 year', '2y': '2 years' },
    contactsNote: 'This place receives customer e-mail and phone: another person with the right to handle personal data must approve.',
    submitted: 'Sent for approval. Once approved, press "Get the key" to see it once.',
  },
  rotate: {
    action: 'Replace key',
    title: 'Replace key {{key}}',
    description: 'A new key with the same use. The old one keeps working for a while, then stops.',
    overlap: 'The old key keeps working for',
    overlapHint: 'so there is time to replace it at {{name}}.',
    overlapDays: { '1': '1 day', '7': '7 days', '30': '30 days' },
  },
  revoke: {
    action: 'Revoke',
    title: 'Revoke key {{key}}',
    warning: '{{name}} can no longer fetch data with this key, starting now.',
    done: 'Revoked.',
  },
  resume: {
    action: 'Unlock key',
    title: 'Unlock key {{key}}',
    why: 'Why it was suspended: {{reason}}.',
    warning: 'Only unlock it when you are sure the key was not exposed. If in doubt, revoke it and create a new key.',
    submit: 'Unlock',
    done: 'The key works again.',
    exposed: 'This key was sent inside a web address. Revoke it and create a new key.',
  },
  secret: {
    title: 'New access key',
    warning:
      'Copy the key and paste it into {{name}} now. It is shown only once: once this window is closed nobody can see it again. If it is lost, replace the key.',
    key: 'Key',
    address: 'Fetch data from',
    takeKey: 'Get the key',
  },
  approval: {
    contactsTitle: 'Before approving: customer e-mail and phone at {{name}}',
    contactsHint: 'You hold the right to handle personal data. Confirm each point; your answers are kept with the approval.',
    waitSecond: 'Another person who manages the reporting API must approve it. They will find it on this page or in Approvals.',
    waitPersonalData: 'This sends customer e-mail and phone: another person with the right to handle personal data must approve it. They will find it on this page or in Approvals.',
    pendingHint: 'Changes to what a place receives and new keys need a second approval. A new key is shown once, when it is taken.',
  },
  history: {
    action: 'History',
    title: 'History of {{name}}',
    calls24h: 'Fetches in 24 hours',
    rowsToday: 'Rows sent today',
    budget: 'Daily limit {{budget}}',
    errors24h: 'Errors in 24 hours',
    onlyErrors: 'Only errors',
    col: { at: 'Time', data: 'Data', rows: 'Rows', contacts: 'With e-mail, phone', result: 'Result', from: 'From address', key: 'Key' },
    empty: 'No fetch in the last 30 days.',
    noErrors: 'No error in the last 30 days.',
    events: 'Events',
    event: {
      'reporting_key.suspend': 'Key suspended: {{reason}}',
      'reporting_key.resume': 'Key unlocked',
      'reporting_checkpoint.reset': 'Sent again in full',
      'reporting_key.rotate': 'Key replaced',
      'reporting_key.revoke': 'Key revoked',
      'reporting_consumer.change': 'Data received changed',
      other: 'Change',
    },
    other: 'Other',
    page: 'Page {{page}} of {{pages}}',
    note: 'Last 30 days. The data itself is never recorded.',
  },
  result: {
    OK: 'Succeeded',
    WRONG_SECRET: 'Wrong key',
    REVOKED: 'Key revoked',
    EXPIRED: 'Key expired',
    SUSPENDED: 'Key suspended',
    NETWORK_NOT_ALLOWED: 'Address not allowed',
    KEY_WITHOUT_ADDRESSES: 'The key has no address list yet',
    DATASET_NOT_ALLOWED: 'The place does not receive this kind of data',
    RATE_LIMITED: 'Too many calls, held back',
    UNAVAILABLE: 'Temporarily unavailable',
    KEY_IN_ADDRESS: 'Key sent inside a web address',
    CONSUMER_DISABLED: 'The place is disabled',
    INVALID: 'Request not valid',
    OTHER: 'Failed',
  },
  reset: {
    action: 'Sync again from the start',
    title: 'Sync {{name}} again from the start',
    pick: 'Kinds of data',
    warning:
      'From the next fetch, all the chosen data is sent again. {{name}} updates its records and creates no duplicates. This can take a while; follow it under "Up to date".',
    contactsWarning: 'This sends customer e-mail and phone again: another person with the right to handle personal data must approve.',
    submit: 'Sync again',
    done: 'Starts again from the next fetch: {{list}}.',
    proposed: 'Sent for approval: {{list}}.',
  },
  parked: {
    action: 'Rows not received',
    title: 'Rows {{name}} did not receive',
    help:
      '{{name}} failed to take these rows several times, so they were set aside and the rest keeps updating. Usually a field at {{name}} lacks an option, or a related record is missing. Fix it at {{name}}, then press "Send again".',
    col: { data: 'Kind of data', record: 'Record', attempts: 'Attempts', last: 'Last attempt' },
    resend: 'Send again',
    resent: 'It will be sent at the next fetch.',
    empty: 'No row is set aside.',
  },
  reconcile: {
    action: 'Compare figures',
    title: 'Figures sent to {{name}}',
    help:
      'Compare with the counts at {{name}}. If they differ, records at {{name}} may have been edited or deleted by hand; turn on the weekly full send or use "Sync again from the start".',
    rows: 'Rows',
    total: 'Total amount',
    byStatus: 'By status',
    empty: 'This place receives no data yet.',
  },
  signals: {
    action: 'Notify on changes',
    title: 'Notify as soon as data changes',
    help: 'Want {{name}} to update almost at once? Enter an address that receives a signal for each kind of data. Only a "new data" signal is sent, never the data. The address must start with https and be reachable from the Internet; if {{name}} runs only inside a private network, let it fetch on its schedule.',
    field: 'Signal address',
    placeholder: 'https://…',
    secret: 'Keep this address secret: anyone who knows it can start a workflow at {{name}}.',
    save: 'Save',
    remove: 'Remove',
    change: 'Change',
    saved: 'Saved.',
    removed: 'Removed.',
    none: 'Not set',
    failing: 'Failing {{count}} times in a row',
    empty: 'This place receives no data yet.',
  },
  rename: {
    action: 'Rename',
    title: 'Rename {{name}}',
    done: 'Saved.',
  },
  customer: {
    sharingTitle: 'Contact details sent to the customer-management system',
    withhold: 'Do not send contact details to the customer-management system',
    withholdHelp: 'Turn on when the customer asks. Their e-mail, phone and name are removed from the receiving places at the next fetch.',
    on: 'On',
    off: 'Off',
    withheldOn: "The customer's e-mail, phone and name are not sent.",
    withheldOff: 'Contact details are sent to the places allowed to receive them.',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    turnOnTitle: 'Do not send the contact details of {{name}}',
    turnOffTitle: 'Send the contact details of {{name}} again',
    turnOffWarning: "Turn off only with the customer's consent. Their e-mail, phone and name are sent again to the places allowed to receive them at the next fetch.",
    turnedOn: "Turned on. The customer's contact details are removed from the receiving places at the next fetch.",
    turnedOff: 'Turned off.',
    disclosuresTitle: "Places that received this customer's data",
    disclosuresHelp: 'From the record of every fetch.',
    disclosuresFailed: 'Could not load the receiving places',
    col: { place: 'Place', since: "Receives the customer's data since", contacts: 'E-mail and phone sent' },
    notYet: 'Not yet',
    never: 'Never',
    contactsSent: '{{times}} times, last {{when}}',
    noPlaces: 'No place receives customer data yet.',
  },
  guide: {
    title: 'Connecting Twenty',
    description: "Twenty fetches the data with its own workflows; nothing is written back here. Steps for Twenty 2.44, no code needed.",
    base: 'Address to fetch from',
    header: 'Header name',
    headerValue: 'Header value',
    headerValueHint: 'The word Bearer, a space, then the key.',
    download: 'Download the connection document',
    downloadHint: 'Describes every kind of data and field, with Vietnamese labels ready for Twenty.',
    downloadFailed: 'Could not download the connection document',
    sample: 'Download the sample answer: {{label}}',
    sampleHint: 'Paste it as the sample answer of the "{{step}}" step in Twenty so you can pick the fields of {{label}}. Each kind of data has its own sample.',
    dataset: 'Example for',
    stepsTitle: 'Two workflows for each kind of data',
    steps: {
      model:
        'In Twenty, under Settings › Data model, create an object for each kind of data (for example {{object}}), with the fields of the connection document. Match records only by id, never by a name or a number.',
      schedule: 'Create the workflow "Tino – sync {{label}}" with the trigger «{{trigger}}», custom schedule {{cron}}.',
      fetch: 'Add the step «{{http}}» named "{{step}}": GET the address below, with the header {{header}} set to the scheduled fetch key.',
      loop: 'Add the step «{{iterator}}» over the received rows; inside it, «{{upsert}}» into {{object}}, matching id with the row id. Map the deleted field to a yes/no field "Deleted".',
      ack: 'Add the step «{{http}}» named "Confirm received": POST to the address below, with the same header and the body below.',
      more: 'Add «{{ifElse}}» "More data?": when more remains, call the twin workflow "Tino – sync {{label}} (more)". The twin has the trigger «{{onCall}}» and the same steps with the "fetch when called" key, and calls itself until nothing is left.',
      never: 'Never turn on "Continue on failure" in these steps. A row that keeps failing is set aside here and shown under "Rows not received".',
    },
    fetchAddress: 'Fetch',
    ackAddress: 'Confirm',
    ackBody: 'Body of the confirmation',
    rows: 'Rows',
    hasMore: 'More data?',
    scheduleTitle: 'Schedule (every 15 minutes, staggered)',
    scheduleCol: { data: 'Kind of data', cron: 'Schedule', object: 'Object in Twenty' },
    notesTitle: 'Good to know',
    notes: {
      money: 'Amounts are whole đồng: use Number fields. For invoices add up the signed amounts, so credit notes subtract.',
      months: 'Group money by the month and day fields sent with each record, never by a date and time field.',
      firstLoad: 'The first fetches send everything; the twin workflow keeps going until it has caught up. Keep 200 rows per fetch.',
      test: 'To test the key, open the address below with the key in the header: it answers with the place, its data and the key expiry.',
    },
    testAddress: 'Test the key',
  },
};
